/**
 * Imandra shared navigation datasource.
 *
 * Canonical Imandra navigation data. Update `imandra-navigation-data.json`
 * here when menus change — every Imandra web app consumes this snapshot
 * (the .dev apps, plus imandra-web/app-www/src/data/navigation-loader.ts and
 * imandra-web/app-universe/src-ui/nav/loader.re).
 */
import navigationDataRaw from './imandra-navigation-data.json'

export type ImandraNavMenuLink = {
  active: boolean
  group: string
  name: string
  description: string
  externalHref: boolean
  href: string
}

export type ImandraNavigationMenus = {
  docs: ImandraNavMenuLink[]
  universe: ImandraNavMenuLink[]
  financialServices: ImandraNavMenuLink[]
  core: ImandraNavMenuLink[]
  markets: ImandraNavMenuLink[]
  connectivity: ImandraNavMenuLink[]
  applications: ImandraNavMenuLink[]
  industries: ImandraNavMenuLink[]
  company: ImandraNavMenuLink[]
  media: ImandraNavMenuLink[]
  signIn: ImandraNavMenuLink[]
}

export type ImandraNavigationData = {
  version: string
  lastUpdated: string
  menus: ImandraNavigationMenus
}

const navigationData = navigationDataRaw as ImandraNavigationData

export const imandraNavigationData: ImandraNavigationData = navigationData

export const imandraNavigationVersion = navigationData.version
export const imandraNavigationLastUpdated = navigationData.lastUpdated

export function imandraNavActiveLinks(
  links: ImandraNavMenuLink[],
): ImandraNavMenuLink[] {
  return links.filter((l) => l.active)
}

export const dataMenuDocs: ImandraNavMenuLink[] = navigationData.menus.docs
export const dataMenuUniverse: ImandraNavMenuLink[] =
  navigationData.menus.universe
export const dataMenuFinancialServices: ImandraNavMenuLink[] =
  navigationData.menus.financialServices
export const dataMenuCore: ImandraNavMenuLink[] = navigationData.menus.core
export const dataMenuMarkets: ImandraNavMenuLink[] =
  navigationData.menus.markets
export const dataMenuConnectivity: ImandraNavMenuLink[] =
  navigationData.menus.connectivity
export const dataMenuApplications: ImandraNavMenuLink[] =
  navigationData.menus.applications
export const dataMenuIndustries: ImandraNavMenuLink[] =
  navigationData.menus.industries
export const dataMenuCompany: ImandraNavMenuLink[] =
  navigationData.menus.company
export const dataMenuMedia: ImandraNavMenuLink[] = navigationData.menus.media
export const dataMenuSignIn: ImandraNavMenuLink[] = navigationData.menus.signIn

export const linkLogin: string =
  (dataMenuSignIn[0]?.href ?? '') + '?return_to=%2Fuser%2Fhome'
export const linkSignUp: string = dataMenuSignIn[1]?.href ?? ''

const workableFromCompany = navigationData.menus.company.find(
  (l) => l.active && l.href.includes('workable.com'),
)

/** Canonical careers URL for “Join our team” style promos (single source). */
export const imandraWorkableCareersUrl: string =
  workableFromCompany?.href ?? 'https://apply.workable.com/imandra/'

export type ImandraFooterEcosystemColumn = {
  id: string
  title: string
  links: ImandraNavMenuLink[]
}

function isHttpHref(href: string): boolean {
  return href.startsWith('http://') || href.startsWith('https://')
}

/** Docs column: active documentation links (order from data), without the hub Overview. */
function footerDocumentationLinks(): ImandraNavMenuLink[] {
  return imandraNavActiveLinks(dataMenuDocs).filter(
    (l) => l.name !== 'Overview',
  )
}

/** Universe column: product links only — Login / Sign Up stays in header CTAs. */
function footerUniverseLinks(): ImandraNavMenuLink[] {
  return imandraNavActiveLinks(dataMenuUniverse).filter(
    (l) => l.name !== 'Login / Sign Up',
  )
}

/** Company column: nav “company” links with careers last. */
function footerCompanyLinks(): ImandraNavMenuLink[] {
  const companyActive = imandraNavActiveLinks(dataMenuCompany)
  const careers = companyActive.filter((l) => l.href.includes('workable.com'))
  const rest = companyActive.filter((l) => !l.href.includes('workable.com'))
  return [...rest, ...careers]
}

/** Grouped columns for marketing footers — same menus as `imandra-web` app-www nav data. */
export function getImandraFooterEcosystemColumns(): ImandraFooterEcosystemColumn[] {
  const columns: ImandraFooterEcosystemColumn[] = [
    {
      id: 'universe',
      title: 'Imandra Universe',
      links: footerUniverseLinks(),
    },
    {
      id: 'financial-services',
      title: 'Financial Services',
      links: imandraNavActiveLinks(dataMenuFinancialServices),
    },
    {
      id: 'applications',
      title: 'Applications',
      links: imandraNavActiveLinks(dataMenuApplications),
    },
    {
      id: 'industries',
      title: 'Industries',
      links: imandraNavActiveLinks(dataMenuIndustries),
    },
    {
      id: 'documentation',
      title: 'Documentation',
      links: footerDocumentationLinks(),
    },
    {
      id: 'company',
      title: 'Company',
      links: footerCompanyLinks(),
    },
    {
      id: 'media',
      title: 'Media',
      links: imandraNavActiveLinks(dataMenuMedia),
    },
  ]

  return columns.filter((c) => c.links.length > 0)
}

export function imandraFooterLinkOpensNewTab(
  link: ImandraNavMenuLink,
): boolean {
  return link.externalHref || isHttpHref(link.href)
}
